import {PayloadAction} from '@reduxjs/toolkit';
import {ForkEffect, call, put, takeLatest} from 'redux-saga/effects';

import * as log from '../../../../logger';
import {authenticate} from '../userApi';
import {requesting, fail} from '../userSlice';
import {getResponseStatus} from '../utils/apiResponses';

import {requestLogin} from './loginSaga';

type BaseAuthenticationPayload = {
  email: string;
  password: string;
};

type BaseAuthenticationAction = PayloadAction<
  BaseAuthenticationPayload,
  'user/requestBaseAuthentication'
>;

function requestBaseAuthentication(
  payload: BaseAuthenticationPayload,
): BaseAuthenticationAction {
  return {
    type: 'user/requestBaseAuthentication',
    payload,
  };
}

function* requestBaseAuthenticationSaga({payload}: BaseAuthenticationAction) {
  const {email, password} = payload;

  try {
    const {token, user} = yield call(authenticate, email, password);
    const credentials = {
      user,
      organization: user.organization,
    };

    yield put(requestLogin({token, credentials}));
  } catch (error) {
    const status: string =
      error instanceof Response
        ? yield call(getResponseStatus, error)
        : 'unreachableServer';

    if (status === 'unreachableServer') {
      yield call(log.error, 'Unknown auth error', {error});
    }

    yield put(fail(status));
  }
}

type AuthenticationPayload = {
  email: string;
  password: string;
  isSamlConnection: boolean;
};

type AuthenticationAction = PayloadAction<
  AuthenticationPayload,
  'user/requestAuthentication'
>;

function requestAuthentication(
  payload: AuthenticationPayload,
): AuthenticationAction {
  return {
    type: 'user/requestAuthentication',
    payload,
  };
}

function* requestAuthenticationSaga({payload}: AuthenticationAction) {
  const {email, password} = payload;

  yield put(requesting());

  try {
    yield put(requestBaseAuthentication({email, password}));
  } catch {
    yield put(fail('loginSchemeError'));
  }
}

function* watchRequestAuthentication(): Generator<
  ForkEffect<void>,
  void,
  unknown
> {
  yield takeLatest(
    'user/requestBaseAuthentication',
    requestBaseAuthenticationSaga,
  );
  yield takeLatest('user/requestAuthentication', requestAuthenticationSaga);
}

export {requestAuthentication};

export default watchRequestAuthentication;
